// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type { BankUploadTemplateEntity } from '@client/banks/utils/download-bank-upload-template';
import type { $ZodIssue } from 'zod/v4/core';
import type { BankUploadParseError, BankUploadParseIssueKind } from './types';

const isMissingValueIssue = (issue: $ZodIssue): boolean => {
  if (issue.code === 'too_small') {
    return true;
  }

  if (issue.code !== 'invalid_type') {
    return false;
  }

  const received = 'received' in issue ? String(issue.received) : '';

  return (
    received === 'undefined' ||
    received === 'null' ||
    issue.message.toLowerCase().includes('undefined') ||
    issue.message.toLowerCase().includes('null')
  );
};

export const mapZodIssueKind = (issue: $ZodIssue): BankUploadParseIssueKind => {
  if (issue.code === 'custom' && issue.message.toLowerCase().includes('future')) {
    return 'future_date';
  }

  if (issue.code === 'invalid_value') {
    return 'invalid_enum';
  }

  if (isMissingValueIssue(issue)) {
    return 'required';
  }

  if (
    issue.code === 'invalid_type' &&
    'expected' in issue &&
    (issue.expected === 'number' || issue.expected === 'int')
  ) {
    return 'invalid_number';
  }

  return 'invalid';
};

export const mapZodIssueToParseError = (
  issue: $ZodIssue,
  entity: BankUploadTemplateEntity,
  row: number,
  sheet: string,
): BankUploadParseError => {
  const fieldPath = issue.path.map(String).join('.');
  const issueKind = mapZodIssueKind(issue);

  return {
    code: 'row_invalid',
    issueKind,
    entity,
    sheet,
    row,
    field: fieldPath || undefined,
  };
};
